import { ref, reactive, onMounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { enumSelect, listCommon } from '@/util/vueutil/commonList.js'
import { request } from '@/util/globalContext'

export default function useHomeIndex(listRef, orderListRef) {
    // 检车待办查询条件
    const searchForm = reactive({
        plateNumber: '',
        userName: '',
        checkStatus: ''
    })

    // 订单查询条件
    const orderSearchForm = reactive({
        orderId: '',
        orderStatus: ''
    })

    // 共用列表工具（检车待办）
    const {
        oneShow,
        listShow,
        handleReset,
        handleSelectionChange,
        handleCurrentRowChange
    } = listCommon(searchForm)

    // 订单相关也独立一份重置
    const {
        handleReset: handleOrderReset
    } = listCommon(orderSearchForm)

    const orderDialogVisible = ref(false)
    const orderDialogType = ref('view')
    const orderFormRef = ref(null)
    const orderItems = ref([])

    const ORDER_FORM_DEFAULT = {
        id: '',
        orderId: '',
        name: '',
        phone: '',
        areaNames: '',
        addressDetail: '',
        items: '',
        ygTotalWeight: '',
        ygTotalAmount: '',
        orderStatus: '',
        orderStatusName: '',
        remark: ''
    }
    const orderFormData = reactive({ ...ORDER_FORM_DEFAULT })

    const orderFormRules = reactive({
        orderStatus: [
            { required: true, message: '请选择订单状态', trigger: 'change' }
        ]
    })

    const Options = ref({
        wxOrderStatus: []
    })

    // 存储空间状态
    const storageWarning = ref(false)

    // ============ 检车待办列表加载 ============
    const handleLoadList = async (paginationParams) => {
        if (!listRef.value) return
        if (!listRef.value.isInited) {
            await nextTick()
            if (!listRef.value.isInited) {
                ElMessage.warning('列表组件初始化中，请稍后重试')
                return
            }
        }

        listRef.value.setLoading(true)

        try {
            const params = {
                ...paginationParams,
                ...searchForm
            }
            const res = await request.post('/home/getAppointmentTodoList', params)

            if (res.code === 200) {
                listRef.value.updateListData(res.data.records || [])
                listRef.value.updatePagination({
                    pageNum: paginationParams.pageNum,
                    pageSize: paginationParams.pageSize,
                    total: res.data.total || 0
                })
            } else {
                ElMessage.error('列表加载失败：' + (res.msg || '接口异常'))
                listRef.value.updateListData([])
                listRef.value.updatePagination({ ...paginationParams, total: 0 })
            }
        } catch (error) {
            ElMessage.error('列表加载失败：网络异常')
            listRef.value.updateListData([])
            listRef.value.updatePagination({ ...paginationParams, total: 0 })
        } finally {
            listRef.value.setLoading(false)
        }
    }

    // ============ 订单列表加载（保留原有逻辑）============
    const handleLoadOrderList = async (paginationParams) => {
        if (!orderListRef.value) return
        if (!orderListRef.value.isInited) {
            await nextTick()
            if (!orderListRef.value.isInited) {
                ElMessage.warning('列表组件初始化中，请稍后重试')
                return
            }
        }

        orderListRef.value.setLoading(true)

        try {
            const params = {
                ...paginationParams,
                ...orderSearchForm
            }
            const res = await request.post('/home/getOrderList', params)

            if (res.code === 200) {
                orderListRef.value.updateListData(res.data.records || [])
                orderListRef.value.updatePagination({
                    pageNum: paginationParams.pageNum,
                    pageSize: paginationParams.pageSize,
                    total: res.data.total || 0
                })
            } else {
                ElMessage.error('列表加载失败：' + (res.msg || '接口异常'))
                orderListRef.value.updateListData([])
                orderListRef.value.updatePagination({ ...paginationParams, total: 0 })
            }
        } catch (error) {
            ElMessage.error('列表加载失败：网络异常')
            orderListRef.value.updateListData([])
            orderListRef.value.updatePagination({ ...paginationParams, total: 0 })
        } finally {
            orderListRef.value.setLoading(false)
        }
    }

    const getOrderDetail = async (id) => {
        try {
            const res = await request.get('/home/getOrderDetail', { params: { id } })
            if (res.code === 200) {
                const order = res.data.order || {}
                orderFormData.id = order.id
                orderFormData.orderId = order.orderId
                orderFormData.name = order.name
                orderFormData.phone = order.phone
                orderFormData.areaNames = order.areaNames
                orderFormData.addressDetail = order.addressDetail
                orderFormData.items = order.items
                orderFormData.ygTotalWeight = order.ygTotalWeight
                orderFormData.ygTotalAmount = order.ygTotalAmount
                orderFormData.orderStatus = order.orderStatus
                orderFormData.orderStatusName = order.orderStatusName
                orderFormData.remark = order.remark

                orderItems.value = res.data.items || []
            } else {
                ElMessage.error(res.msg || '获取订单详情失败')
            }
        } catch (error) {
            ElMessage.error('获取订单详情失败：网络异常')
        }
    }

    const handleViewOrder = async (row) => {
        orderDialogType.value = 'view'
        await getOrderDetail(row.id)
        orderDialogVisible.value = true
    }

    const handleEditOrder = async (row) => {
        orderDialogType.value = 'edit'
        await getOrderDetail(row.id)
        orderDialogVisible.value = true
    }

    const handleSubmitOrder = async () => {
        if (!orderFormRef.value) return

        const isValid = await new Promise((resolve) => {
            orderFormRef.value.validate((valid) => {
                resolve(valid);
            });
        });
        if (!isValid) return;

        try {
            const params = {
                id: orderFormData.id,
                orderStatus: orderFormData.orderStatus,
                remark: orderFormData.remark
            }
            const res = await request.post('/home/updateOrder', params)

            if (res.code === 200) {
                ElMessage.success('修改成功')
                orderDialogVisible.value = false
                const pagination = orderListRef.value.getPagination()
                handleLoadOrderList(pagination)
            } else {
                ElMessage.error('修改失败：' + (res.msg || '接口异常'))
            }
        } catch (error) {
            ElMessage.error('修改失败：网络异常')
        }
    }

    const getStatusEnum = async () => {
        try {
            const res = await enumSelect('enumType', 'wx_order_status').catch(() => []);
            Options.value.wxOrderStatus = res;
        } catch (error) {
            console.log('初始化枚举失败:', error)
        }
    }

    // 检车状态标签颜色
    const getCheckStatusTagType = (status) => {
        switch (status) {
            case '0': return 'primary'
            case '1': return 'warning'
            case '2': return 'warning'
            case '3': return 'warning'
            case '4': return 'warning'
            case '5': return 'success'
            case '6': return 'success'
            case '7': return 'info'
            default: return 'info'
        }
    }

    // 数据脱敏处理
    const maskName = (name) => {
        if (!name) return '-'
        if (name.length <= 1) return name
        return name[0] + '*'.repeat(name.length - 1)
    }
    const maskPlate = (plate) => {
        if (!plate) return '-'
        if (plate.length <= 4) return plate
        return plate.substring(0, 2) + '*'.repeat(plate.length - 4) + plate.substring(plate.length - 2)
    }
    const maskVin = (vin) => {
        if (!vin) return '-'
        if (vin.length <= 7) return vin
        return vin.substring(0, 4) + '*'.repeat(vin.length - 7) + vin.substring(vin.length - 3)
    }
    const maskPhone = (phone) => {
        if (!phone) return '-'
        if (phone.length !== 11) return phone
        return phone.substring(0, 3) + '****' + phone.substring(7)
    }

    // 检查存储空间状态
    const checkStorageStatus = async () => {
        try {
            const res = await request.get('/wxapi/getStorageStatus')
            if (res.code === 200) {
                const data = res.data
                if (data.isFull || data.imageCount >= data.maxImageCount) {
                    storageWarning.value = true
                }
            }
        } catch (error) {
            console.log('获取存储空间状态失败:', error)
        }
    }

    onMounted(async () => {
        await nextTick();
        await getStatusEnum()
        await checkStorageStatus()

        setTimeout(() => {
            if (listRef.value?.isInited) {
                const pagination = listRef.value.getPagination()
                handleLoadList(pagination)
            }
            if (orderListRef.value?.isInited) {
                const pagination = orderListRef.value.getPagination()
                handleLoadOrderList(pagination)
            }
        }, 50)
    })

    return {
        searchForm,
        orderSearchForm,
        orderItems,
        oneShow,
        listShow,
        handleReset,
        handleOrderReset,
        handleSelectionChange,
        handleCurrentRowChange,
        handleLoadList,
        handleLoadOrderList,
        handleViewOrder,
        handleEditOrder,
        handleSubmitOrder,
        orderDialogVisible,
        orderDialogType,
        orderFormRef,
        orderFormData,
        orderFormRules,
        Options,
        getCheckStatusTagType,
        storageWarning,
        maskName,
        maskPlate,
        maskVin,
        maskPhone
    }
}
